import { DownOutlined } from '@ant-design/icons';
import { HasAccess } from "@permify/react-role";
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Dropdown,
  Empty,
  Form,
  Input,
  Menu,
  message,
  Popover,
  Radio,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Tabs,
  Tag,
  Tooltip
} from 'antd';
import locale from 'antd/es/date-picker/locale/es_ES';
import _ from 'lodash';
import debounce from 'lodash/debounce';
import moment from 'moment';
import 'moment/locale/es';
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from 'react-router-dom';
import { handlers } from '../../helpers/helpers';
import { getCookie, listenerCookie } from "../../services/cookies";
import { traceabilityReport } from '../../services/exam';
import { getChangeStatus, getServices } from "../../services/general";
import { consultationService } from "../../services/medicalConsultation";
import evaluationTypes from "../../utils/evaluationType.json";
import { exportTraceabilitySamplesToExcel } from '../../utils/excelUtils';
import groupedExams from '../../utils/groupedExams.json';
import { normalizeDocumentNumber } from "../../utils/inputValidations";
import { PRESTATIONS_SEARCH_DEBOUNCE_MS, isIncompleteSearchTerm } from "../../utils/searchConfig";
import FeedbackModal from "./components/feedbackModal";
import SwitchReport from './components/SwitchReport';
import settings from './countrySettings.json';
import useReports from './hooks/useReports';
import UnifiedSearchFilters from '../../components/UnifiedSearchFilters/UnifiedSearchFilters';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { format } = require('rut.js');

const MedicalConsultationReportPage = () => {
  const urlSearchParams = new URLSearchParams(window.location.search);
  const navigate = useNavigate();
  const branchOfficeId = getCookie('branch_office_id');
  const queryParams = {
    ...Object.fromEntries(urlSearchParams.entries()),
    branch_office_id: branchOfficeId,
  };
  const [form] = Form.useForm();
  const [services, setServices] = useState([]);
  const [filters, setFilters] = useState(Object.keys(queryParams).length !== 0 ? queryParams : {});
  const [refresh, setRefresh] = useState();
  const [loading, setLoading] = useState({
    table: false,
    changesTable: false,
    button: false,
    changeStatus: false,
    traceabilityReport: false,
  });
  const [reports, setReports] = useState();
  const [reportChanges, setReportChanges] = useState();
  const [currentPage, setCurrentPage] = useState(1);
  const [changesCurrentPage, setChangesCurrentPage] = useState(1);
  const [feedbackModal, setFeedbackModal] = useState({
    open: false,
    type: 'success'
  });
  const [changeStatus, setChangeStatus] = useState([]);
  const { TabPane } = Tabs;
  const countryCode = getCookie('country_code')
  const roles = getCookie('roles');
  const { t } = useTranslation();

  const {
    downloadOrderPeruExams,
  } = useReports(countryCode, reports, settings);

  const getChangeStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'done':
        return 'status-success';
      case 'pending':
        return 'status-pending';
      case 'failed':
        return 'status-error';
      default:
        return '';
    }
  };

  const getTraceabilityReport = async () => {
    setLoading((loading) => ({ ...loading, traceabilityReport: true }));
    try {
      const formValues = form.getFieldsValue();

      let startDate = queryParams.date_start;
      let endDate = queryParams.date_end;

      if (formValues.dateRange && formValues.dateRange[0] && formValues.dateRange[1]) {
        startDate = formValues.dateRange[0].format('YYYY-MM-DD');
        endDate = formValues.dateRange[1].format('YYYY-MM-DD');
      }
      if (!startDate || !endDate) {
        message.warning({
          content: 'Por favor selecciona un rango de fechas',
          style: {
            marginTop: '30vh',
          },
        });
        setLoading((loading) => ({ ...loading, traceabilityReport: false }));
        return;
      }

      const response = await traceabilityReport({
        startDate,
        endDate,
      });
      if (response?.length > 0) {
        await exportTraceabilitySamplesToExcel(response, 'seguimiento_muestras');
        message.success({
          content: 'Reporte descargado exitosamente',
          style: {
            marginTop: '30vh',
          },
        });
      } else {
        message.warning({
          content: 'No hay datos disponibles para el rango de fechas seleccionado',
          style: {
            marginTop: '30vh',
          },
        });
      }
    } catch (error) {
      console.error('Error', error);
      message.error({
        content: 'Error al descargar el reporte de seguimiento',
        style: {
          marginTop: '30vh',
        },
      });
    }
    setLoading((loading) => ({ ...loading, traceabilityReport: false }));
  };


  const fetchChangeStatus = useCallback(async () => {
    try {
      setLoading(prev => ({ ...prev, changeStatus: true }));

      const response = await getChangeStatus();

      if (response.length) {
        setChangeStatus(response);
      } else {
        throw new Error(response);
      }
    } catch (error) {
      console.error('Error en fetchChangeStatus', error);
      message.error({
        content: 'Error al obtener Estados de cambio',
        style: {
          marginTop: '45vh',
        },
      });
    } finally {
      setLoading(prev => ({ ...prev, changeStatus: false }));
    }
  }, []);

  useEffect(() => {
    fetchChangeStatus();
  }, [fetchChangeStatus]);

  const onFinishFilters = (values) => {
    const data = {};

    if (values.dateRange) {
      data['start_date'] = values.dateRange[0].format('YYYY-MM-DD')
      data['end_date'] = values.dateRange[1].format('YYYY-MM-DD')
    }

    if (values.dates && values.dates[0]) {
      data['start_date'] = values.dates[0].format('YYYY-MM-DD');
      data['end_date'] = values.dates[1].format('YYYY-MM-DD');
    }

    if (values.start_date && values.end_date) {
      data['start_date'] = values.start_date;
      data['end_date'] = values.end_date;
    }

    if (values.query) {
      data['query'] = normalizeDocumentNumber(values.query);
    }

    if (values.patient_document_number) {
      data['patient_document_number'] = normalizeDocumentNumber(values.patient_document_number);
    }

    if (values.patient_names) {
      data['patient_names'] = values.patient_names;
    }

    if (values.company_id) {
      data['company_id'] = values.company_id;
    }

    if (values.outsourcing_id) {
      data['outsourcing_id'] = values.outsourcing_id;
    }

    if (values.third_company_id) {
      data['third_company_id'] = values.third_company_id;
    }

    if (values.reservation_number) {
      data['reservation_number'] = values.reservation_number;
    }

    if (values.protocol_id) {
      data['protocol_id'] = values.protocol_id;
    }

    if (values.evaluation_types) {
      data['evaluation_types'] = values.evaluation_types;
    }

    if (values.changeStatus) {
      data['change_status'] = values.changeStatus;
    }

    data['all_branch_offices'] = values.allBranchOffices ?? false;

    if (values.serviceId) {
      const [service_id, service_type] = values.serviceId.split(':');
      data['service_id'] = service_id;
      data['service_type'] = service_type;
    }

    if (!values.allBranchOffices) {
      data['branch_office_id'] = branchOfficeId;
    }

    handlers.replaceUrlQueryString(navigate, data);
    setCurrentPage(1);
    setChangesCurrentPage(1);
    setFilters(data);
  };


  const initializeFormOnQueryParam = useCallback((params) => {
    let formParams = {};
    if (countryCode === 'PE') {
      formParams = {
        patient_document_number: params['patient_document_number'] || null,
        patient_names: params['patient_names'] || null,
        company_id: params['company_id'] ? parseInt(params['company_id']) : undefined,
        outsourcing_id: params['outsourcing_id'] ? parseInt(params['outsourcing_id']) : undefined,
        third_company_id: params['third_company_id'] ? parseInt(params['third_company_id']) : undefined,
        serviceId: params['service_id'] ? `${params['service_id']}:${params['service_type']}` : undefined,
        changeStatus: params['change_status'],
        allBranchOffices: params['all_branch_offices'] === 'true',
        dates: params['start_date'] && params['end_date'] ? [moment(params['start_date']), moment(params['end_date'])] : [moment(), moment()]
      };
    } else {
      formParams = {
        query: params['query'] || null,
        dateRange: params['start_date'] !== null && params['end_date'] !== null ? [moment(params['start_date']), moment(params['end_date'])] : [moment(), moment()],
        serviceId: params['service_id'] ? `${params['service_id']}:${params['service_type']}` : undefined,
        changeStatus: params['change_status'],
        allBranchOffices: params['all_branch_offices'] === 'true'
      };
    }

    form.setFieldsValue(formParams)
    return params;
  }, [queryParams, countryCode, form]);

  const fetchReports = useCallback(async (filters) => {
    setLoading(prev => ({
      ...prev,
      table: true
    }));
    try {
      const response = await consultationService.getReports(filters);
      setReports(response)
    } catch (error) {
      console.error(error);
      message.error({
        content: 'Ocurrió un error al obtener reportes.',
        style: {
          marginTop: '30vh',
          fontSize: 16
        }
      });
    } finally {
      setLoading(prev => ({
        ...prev,
        table: false
      }));
    }
  }, [filters, refresh, currentPage]);

  const fetchServices = debounce(async (name) => {
    if (isIncompleteSearchTerm(name)) return;
    const response = await getServices({
      ...(name && { query: name }),
      ...(countryCode === 'PE' && { type: 'Exam' }),
      limit: 50
    });
    setServices(response);
    if (queryParams['service_id']) {
      form.setFieldsValue({
        serviceId: `${queryParams['service_id']}:${queryParams['service_type']}`
      });
    }
  }, PRESTATIONS_SEARCH_DEBOUNCE_MS);

  const doDownloadReport = async (item, type, occupationalType, admissionPatientExamId, variant) => {
    setLoading(prev => ({
      ...prev,
      button: true
    }));
    try {
      if (settings.report.mergedMedicalConsultation[countryCode] && item.attached_medical_consultation_ids?.length && !item.has_template) {
        await downloadOrderPeruExams(item, type, occupationalType)
      } else {
        handlers.downloadSignedReport({
          id: item.id,
          type,
          occupationalType,
          fileName: 'informe.pdf',
          action: 'open',
          admission_patient_exam_id: admissionPatientExamId,
          variant,
        });
      }
      message.success('Informe descargado exitosamente');
    } catch (error) {
      console.error(error);
      message.error({
        content: 'Ocurrió un error al descargar el informe.',
        style: {
          marginTop: '30vh',
          fontSize: 16
        }
      });
    } finally {
      setLoading(prev => ({
        ...prev,
        button: false
      }));
    }
  };

  const fetchReportsChanges = useCallback(async (filters) => {
    if (!settings.report.reportsChanges[countryCode]) {
      setReportChanges([]);
      return;
    }

    setLoading(prev => ({
      ...prev,
      changesTable: true
    }));
    try {
      const response = await consultationService.listReportsChanges(filters);

      setReportChanges(response)
    } catch (error) {
      console.error(error);
      message.error({
        content: 'Ocurrió un error al obtener los cambios en informes',
        style: {
          marginTop: '30vh',
          fontSize: 16
        }
      });
    } finally {
      setLoading(prev => ({
        ...prev,
        changesTable: false
      }));
    }
  }, []);

  useEffect(() => {
    fetchReports({ ...filters, page: currentPage });
    fetchReportsChanges({ ...filters, page: changesCurrentPage });
    fetchServices('');
    initializeFormOnQueryParam(filters);
  }, [fetchReports, fetchReportsChanges, refresh, currentPage, changesCurrentPage]);

  useEffect(() => {
    listenerCookie((data) => {
      if (data.name === 'branch_office_id') {
        const urlSearchParams = new URLSearchParams(window.location.search);
        const _filters = {
          ...Object.fromEntries(urlSearchParams.entries()),
          branch_office_id: data.value
        };
        setFilters(_filters);
        handlers.replaceUrlQueryString(navigate, _filters);
      }
    });
  }, []);

  const loadMore = (event) => {
    const direction = event.target.value;
    const _currentPage = direction === 'next' ? currentPage + 1 : currentPage - 1;
    setCurrentPage(_currentPage);
  };

  const loadMoreReportChanges = (event) => {
    const direction = event.target.value;
    const _changesCurrentPage = direction === 'next' ? changesCurrentPage + 1 : changesCurrentPage - 1;
    setChangesCurrentPage(_changesCurrentPage);
  };

  const handleSaveReportChange = async (item) => {
    try {
      setLoading(prev => ({
        ...prev,
        button: true
      }));
      await consultationService.applyReportchanges({
        ...item,
      });
      setFeedbackModal({ open: true, type: 'success' });
    } catch (error) {
      console.error(error);
      setFeedbackModal({ open: true, type: 'error' });
    } finally {
      setLoading(prev => ({
        ...prev,
        button: false
      }));
    }
  }


  const customTableHeader = () =>
    <Row className="table-header">
      <Col
        lg={3}
        xs={12}
        className="align-item-center-start p-10"
      >
        <h3>
          {t('medicalConsultations.reportPage.reservationCol')}
        </h3>
      </Col>
      <Col
        lg={4}
        xs={12}
        className="center-item p-10"
      >
        <h3 className="text-center">Compañía</h3>
      </Col>
      <Col
        lg={5}
        xs={12}
        className="center-item p-10"
      >
        <h3 className="text-center">
          {t('medicalConsultations.reportPage.title')}
        </h3>
      </Col>
      <Col
        lg={3}
        xs={12}
        className="center-item p-10"
      >
        <h3 className="text-center">Identificador</h3>
      </Col>
      <Col
        lg={5}
        xs={12}
        className="center-item p-10"
      >
        <h3 className="text-center">
          {t('medicalConsultations.reportPage.serviceCol')}
        </h3>
      </Col>
      <Col
        lg={4}
        xs={12}
        className="center-item p-10"
      >
        <h3 className="text-center">Mostrar informe</h3>
      </Col>
    </Row>

  const customTableBody = (item, index) =>
    <Row className="table-body mb-15" key={index}>
      <Col
        lg={4}
        xs={12}
        className="align-item-center-start p-10"
      >
        <h3 className="hide-desktop text-center">
          {t('medicalConsultations.reportPage.reservationCol')}
        </h3>
        <p className="mb-0">{item.reservation_number}</p>
      </Col>
      <Col
        lg={3}
        xs={12}
        className="center-item p-10"
      >
        <h3 className="hide-desktop text-center">
          Compañía
        </h3>
        <p className="mb-0 text-center">{item.company_name}</p>
      </Col>
      <Col
        lg={5}
        xs={12}
        className="center-item p-10"
      >
        <h3 className="hide-desktop text-center">
          {t('medicalConsultations.reportPage.title')}
        </h3>
        <p className="mb-0">{`${item.names} ${item.last_name}`}</p>
      </Col>
      <Col
        lg={3}
        xs={12}
        className="center-item p-10"
      >
        <h3 className="hide-desktop text-center">Identificador</h3>
        {
          item.document_type === 'rut' ?
            <p className="mb-0">{format(item.document_number)}</p>
            :
            <p className="mb-0">{item.document_number}</p>
        }
      </Col>
      <Col
        lg={5}
        xs={12}
        className="center-item p-10"
      >
        <h3 className="hide-desktop text-center">
          {t('medicalConsultations.reportPage.serviceCol')}
        </h3>
        <p className="mb-0">{item.consultation_name}</p>
      </Col>
      <Col
        lg={4}
        xs={12}
        className="center-item p-10"
      >
        <h3 className="hide-desktop text-center">Mostrar informe</h3>
        <Row justify="center" align="middle" gutter={20}>
          <Col>
            {
              roles.includes('branch_office_manager') ? (
                <SwitchReport
                  record={item}
                  setRefresh={setRefresh}
                />
              ) : (
                <Tooltip title="Solo los jefes de sucursales tienen permisos para habilitar o deshabilitar informes">
                  <Switch
                    className="cmt-switch"
                    checked={item.document_visibility}
                    disabled
                  />
                </Tooltip>
              )
            }
          </Col>
          <Col>
            <HasAccess
              roles={['super_usuario', 'branch_office_manager', 'equipo_psicologia',
                'doctor', 'direccion_medica', 'administracion']}
              permissions='downloadReport'
            >
              <Dropdown
                overlay={
                  <Menu>
                    {
                      item.document_file_name ? (
                        <Menu.Item>
                          <Button
                            type="link"
                            onClick={() => doDownloadReport(item, 'signed')}
                          >
                            Descargar informe oficial
                          </Button>
                        </Menu.Item>
                      ) : (
                        <Menu.Item>
                          <Button
                            type="link"
                            onClick={() => {
                              if (item.is_provider) {
                                doDownloadReport(item, 'isl', item.battery_evaluation_type === 0 ? 'company' : 'patient');
                              } else {
                                doDownloadReport(item, 'generated');
                              }
                            }}
                          >
                            Generar informe
                          </Button>
                        </Menu.Item>
                      )
                    }

                    {
                      item.is_provider && (
                        <React.Fragment>
                          {item.has_questions && (
                            <Menu.Item>
                              <Button
                                type="link"
                                onClick={() => doDownloadReport(item, 'isl', 'patient', null, 'surveys')}
                              >
                                Encuestas
                              </Button>
                            </Menu.Item>
                          )}
                          {item.isl_exams?.find(exam => exam.code === 'EVALUACION_MEDICA_ISL') && (
                            <Menu.Item>
                              <Button
                                type="link"
                                onClick={() => doDownloadReport(item, 'isl', 'patient', item.isl_exams.find(exam => exam.code === 'EVALUACION_MEDICA_ISL').id)}
                              >
                                Evaluación Médica
                              </Button>
                            </Menu.Item>
                          )}
                          {item.isl_exams?.find(exam => exam.code === 'DECLARACION_SALUD_ISL') && (
                            <Menu.Item>
                              <Button
                                type="link"
                                onClick={() => doDownloadReport(item, 'isl', 'patient', item.isl_exams.find(exam => exam.code === 'DECLARACION_SALUD_ISL').id)}
                              >
                                Encuesta general de salud
                              </Button>
                            </Menu.Item>
                          )}
                          {item.battery_evaluation_type === 0 && (
                            <React.Fragment>
                              <Menu.Item>
                                <Button
                                  type="link"
                                  onClick={() => doDownloadReport(item, 'isl', 'patient')}
                                >
                                  Informe trabajador
                                </Button>
                              </Menu.Item>
                              <Menu.Item>
                                <Button
                                  type="link"
                                  onClick={() => doDownloadReport(item, 'isl', 'company')}
                                >
                                  Informe empleador
                                </Button>
                              </Menu.Item>
                            </React.Fragment>
                          )}
                          {item.battery_evaluation_type === 1 && (
                            <Menu.Item>
                              <Button
                                type="link"
                                onClick={() => doDownloadReport(item, 'isl', 'patient')}
                              >
                                Informe vigilancia
                              </Button>
                            </Menu.Item>
                          )}
                        </React.Fragment>
                      )
                    }
                  </Menu>
                }
              >
                <Button type="link" onClick={e => e.preventDefault()} loading={loading.button}>
                  <Space>
                    Descargar
                    <DownOutlined />
                  </Space>
                </Button>
              </Dropdown>
            </HasAccess>
          </Col>
        </Row>
      </Col>
    </Row>

  const reportChangesTableHeader = () =>
    <Row className="table-header">
      <Col
        lg={2}
        xs={12}
        className="align-item-center-start p-10"
      >
        <h3>
          {t('medicalConsultations.reportPage.reservationCol')}
        </h3>
      </Col>
      <Col
        lg={2}
        xs={12}
        className="center-item p-10"
      >
        <h3 className="text-center">Informe</h3>
      </Col>
      <Col
        lg={2}
        xs={12}
        className="center-item p-10"
      >
        <h3 className="text-center">Identificador</h3>
      </Col>
      <Col
        lg={3}
        xs={12}
        className="center-item p-10"
      >
        <h3 className="text-center">Compañía</h3>
      </Col>
      <Col
        lg={3}
        xs={12}
        className="center-item p-10">
        <h3 className="text-center">Servicio</h3>
      </Col>
      <Col
        lg={2}
        xs={12}
        className="center-item p-10"
      >
        <h3 className="text-center">Tipo de cambio</h3>
      </Col>
      <Col
        lg={3}
        xs={12}
        className="center-item p-10"
      >
        <h3 className="text-center">Valor de cambio</h3>
      </Col>
      <Col
        lg={3}
        xs={12}
        className="center-item p-10"
      >
        <h3 className="text-center">Estado de cambio</h3>
      </Col>
      <Col
        lg={2}
        xs={12}
        className="center-item p-10"
      >
        <h3 className="text-center">Motivo de rechazo</h3>
      </Col>
      <Col
        lg={2}
        xs={12}
        className="center-item p-10"
      >
        <h3 className="text-center">Fecha y hr de cambio</h3>
      </Col>
    </Row>

  const unfinishedExams = (item) => item.error_details?.unfinishedExams && <Popover
    title="Exámenes pendientes"
    trigger="hover"
    content={
      <div>
        {
          item.error_details.unfinishedExams.map((unfinishedExam, index) =>
            <p key={`${index}-unfinishedExams`}>
              <Link
                to={{
                  pathname: `/examenes/${Object.keys(groupedExams).includes(unfinishedExam.exam_type) ? unfinishedExam.admission_patient_id : unfinishedExam.admission_patient_exam_id}`,
                  search: Object.keys(groupedExams).includes(unfinishedExam.exam_type) ? `?groupType=${unfinishedExam.exam_type}` : '',
                }}
                target="_blank"
                className="mb-5"
              >
                <b>{unfinishedExam.name}:&nbsp;</b>
              </Link>
              {unfinishedExam?.measurements?.join(', ')}
              {unfinishedExam?.procedures?.join(', ')}
            </p>
          )
        }
      </div>
    }
    overlayClassName="sidebar-tooltip"
  >
    <p className="mb-0 text-center">
      {item.failed_reason ? t(`medicalConsultations.reportPage.${item.failed_reason}`) : '-'}
    </p>
  </Popover>

  const validatedExams = (item) => item.error_details?.validatedExams && <Popover
    title="Exámenes con valores reprobatorios"
    trigger="hover"
    content={
      <div>
        {
          item.error_details.validatedExams.map((validatedExam, index) => {
            const reprovedProcedures = validatedExam?.procedures?.map(procedure =>
              (procedure.result_type?.includes('reproves') || procedure.reprovingItems?.includes(procedure?.result?.toLowerCase()))
              && procedure.name)?.filter(Boolean);
            const reprovedMeasurements = validatedExam?.measurements?.map(measurement =>
              (measurement?.result_type?.includes('reproves') || measurement.reprovingItems?.includes(measurement?.result?.toLowerCase()))
              && measurement.name)?.filter(Boolean);

            const reproveds = [...(reprovedProcedures || []), ...(reprovedMeasurements || [])];
            return (
              <div key={`validatedExams-${index}`}>
                {
                  reproveds?.length > 0 &&
                  <p>
                    <Link
                      to={{
                        pathname: `/examenes/${Object.keys(groupedExams).includes(validatedExam.exam_type) ? validatedExam.admission_patient_id : validatedExam.id}`,
                        search: Object.keys(groupedExams).includes(validatedExam.exam_type) ? `?groupType=${validatedExam.exam_type}` : '',
                      }}
                      target="_blank"
                      className="mb-5"
                    >
                      <b>{validatedExam.exam_name}</b>:&nbsp;
                    </Link>
                    {reproveds?.join(', ')}
                  </p>
                }
              </div>
            )
          })
        }
      </div>
    }
    overlayClassName="sidebar-tooltip"
  >
    <p className="mb-0 text-center">
      {item.failed_reason ? t(`medicalConsultations.reportPage.${item.failed_reason}`) : '-'}
    </p>
  </Popover>

  const translateValue = (item) => {
    if (item.change_type === 'company') {
      return item.business_name
    } else if (item.change_type === 'evaluation_type') {
      return evaluationTypes[countryCode]?.find(evaluationType => evaluationType.value?.toString() === item.change_value)?.label
    }
    return item.change_value;
  }

  const reportChangesTableBody = (item, index) =>
    <div key={index} className="table-body mb-15">
      <Row className="table-row-data">
        <Col
          lg={2}
          xs={12}
          className="align-item-center-start p-10"
        >
          <h3 className="hide-desktop text-center">
            {t('medicalConsultations.reportPage.reservationCol')}
          </h3>
          <p className="mb-0">{item.reservation_number}</p>
        </Col>
        <Col
          lg={2}
          xs={12}
          className="align-item-center-start custom-tag-content p-10"
        >
          <h3 className="hide-desktop text-center">Paciente</h3>
          <p className="mb-0 text-center">{item.name} {item.last_name}</p>
          {
            item.branch_office_name && <Tag className='cmt-tag'>{item.branch_office_name}</Tag>
          }
        </Col>
        <Col
          lg={2}
          xs={12}
          className="center-item p-10"
        >
          <h3 className="hide-desktop text-center">Identificador</h3>
          <p className="mb-0 text-center">{item.document_number}</p>
        </Col>
        <Col
          lg={3}
          xs={12}
          className="center-item p-10"
        >
          <h3 className="hide-desktop text-center">Compañía</h3>
          <p className="mb-0 text-center">{item.company_name}</p>
        </Col>
        <Col
          lg={3}
          xs={12}
          className="center-item p-10"
        >
          <h3 className="hide-desktop text-center">Servicio</h3>
          <p className="mb-0 text-center">
            <Link
              to={{
                pathname: `/consulta-medica/${item.medical_consultation_detail_id}`,
              }}
              target="_blank"
              className="mb-5"
            >

              {item.service_name}
            </Link>
          </p>
        </Col>
        <Col
          lg={2}
          xs={12}
          className="center-item p-10"
        >
          <h3 className="hide-desktop text-center">Tipo de cambio</h3>
          <p className="mb-0 text-center">
            {t(`medicalConsultations.reportPage.${item.change_type}`)}
          </p>
        </Col>
        <Col
          lg={3}
          xs={12}
          className="center-item p-10"
        >
          <h3 className="hide-desktop text-center">Valor de cambio</h3>
          <p className="mb-0 text-center">
            {translateValue(item)}
          </p>
        </Col>
        <Col
          lg={3}
          xs={12}
          className="center-item p-10"
        >
          <Row>
            <Col xs={24} lg={24}>
              <h3 className="hide-desktop text-center">Estado de cambio</h3>
              <p className={`mb-0 text-center ${getChangeStatusClass(item.change_status)}`}>
                {t(`medicalConsultations.reportPage.${item.change_status}`)}
              </p>
            </Col>
            <Col xs={24} lg={24}>
              {(item.change_status === 'pending') && (
                <Tooltip
                  placement="right"
                  color="#FFFFFF"
                  title={
                    <div>
                      <strong>{t('medicalConsultations.reportPage.changeReportTooltipTitle')}</strong>
                      <hr />
                      <span>{t('medicalConsultations.reportPage.changeReportTooltipText')}</span>
                    </div>
                  }
                  overlayClassName="change-report-tooltip"
                >
                  <Button
                    className="cmt-button secondary outline mt-10"
                    size="small"
                    onClick={() => handleSaveReportChange(item)}
                    loading={loading.button}
                  >
                    {t('medicalConsultations.reportPage.changeReportButton')}
                  </Button>
                </Tooltip>
              )}
            </Col>
          </Row>
        </Col>
        <Col
          lg={2}
          xs={12}
          className="center-item p-10"
        >
          <h3 className="hide-desktop text-center">Motivo de rechazo</h3>

          {
            item.change_status !== 'done' && (
              (item.error_details?.unfinishedExams || item.error_details?.validatedExams) ?
                unfinishedExams(item) || validatedExams(item)
                :
                <p className="mb-0 text-center">
                  {item.failed_reason ? t(`medicalConsultations.reportPage.${item.failed_reason}`) : '-'}
                </p>
            )
          }
        </Col>
        <Col
          lg={2}
          xs={12}
          className="center-item p-10"
        >
          <h3 className="hide-desktop text-center">Fecha de cambio</h3>
          <p className="mb-0 text-center">{item.date && moment(item.date).format('DD-MM-YYYY HH:mm:ss')}</p>
        </Col>
      </Row>
    </div>

  return (
    <Row justify="center" className="medical-consultation-report-page">
      <Col lg={22} xs={22}>
        <h1 className="ml-5 mb-0 mt-20">{t('medicalConsultations.reportPage.title')}</h1>
        {settings?.report?.useGranularSearchFilters?.[countryCode] ? (
          <UnifiedSearchFilters
            moduleName={t('medicalConsultations.reportPage.title')}
            baseFilters={['company', 'outsourcing', 'thirdCompany', 'branchOffice', 'workerId', 'workerName', 'reservationNumber', 'protocol', 'dateRange', 'evaluationType', 'allBranchOffices']}
            customFilters={[]}
            isCollapsible={true}
            userHasSystemWideAccess={roles?.includes('direccion_medica')}
            onSearch={onFinishFilters}
            loading={loading.table || loading.changesTable}
            onClear={() => {
              setFilters({});
              form.resetFields();
              setRefresh(Date.now());
            }}
            initialValues={{
              dates: [moment(filters.start_date || moment()), moment(filters.end_date || moment())],
              serviceId: filters.service_id ? `${filters.service_id}:${filters.service_type}` : undefined,
              company_id: filters.company_id ? parseInt(filters.company_id) : undefined,
              outsourcing_id: filters.outsourcing_id ? parseInt(filters.outsourcing_id) : undefined,
              third_company_id: filters.third_company_id ? parseInt(filters.third_company_id) : undefined,
              patient_document_number: filters.patient_document_number,
              patient_names: filters.patient_names,
              reservation_number: filters.reservation_number,
              protocol_id: filters.protocol_id ? parseInt(filters.protocol_id) : undefined,
              evaluation_types: filters.evaluation_types ? (Array.isArray(filters.evaluation_types) ? filters.evaluation_types : [filters.evaluation_types]) : undefined,
              changeStatus: filters.change_status,
              branch_office_id: filters.branch_office_id ? parseInt(filters.branch_office_id) : parseInt(getCookie('branch_office_id')),
              allBranchOffices: filters.all_branch_offices === 'true'
            }}
            actionNodes={settings?.report.downloadTraceabilityReport[countryCode] ? [
              <Button onClick={getTraceabilityReport} className="cmt-button primary outline" type="primary" loading={loading.traceabilityReport}>
                Seguimiento de muestras
              </Button>
            ] : []}
            customNodes={[
              <Col xl={6} lg={8} md={12} xs={24}>
                <Form.Item name="serviceId" label={t('medicalConsultations.reportPage.serviceLabel')}>
                  <Select
                    className="cmt-select"
                    placeholder="Todos"
                    showSearch
                    showArrow={false}
                    filterOption={false}
                    allowClear
                    onSearch={fetchServices}
                    notFoundContent={<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<div>No hay coincidencias</div>} />}
                  >
                    {services?.data?.map((item, index) => (
                      <Option key={index} value={`${item.id}:${item.type}`} type={item.type}>{item.name}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>,
              ...(settings?.report.reportsChanges[countryCode] ? [
                <Col xl={6} lg={8} md={12} xs={24} key="change_status">
                  <Spin className='cmt-spin' spinning={loading.changeStatus}>
                    {changeStatus.length > 0 && (
                      <Form.Item label='Estado de cambio' name='changeStatus'>
                        <Select className='cmt-select' placeholder='Selecciona'>
                          {changeStatus.map((item) => (
                            <Option key={item.status} value={item.status}>{item.name}</Option>
                          ))}
                        </Select>
                      </Form.Item>
                    )}
                  </Spin>
                </Col>
              ] : [])
            ]}
          />
        ) : (
          <Form
            form={form}
            onFinish={onFinishFilters}
            size="large"
            layout="vertical"
            className='cmt-form'
          >
            {settings?.report.downloadTraceabilityReport[countryCode] &&
              <Row justify='end' className="mt-15 mb-lg-0 mb-md-20 mb-sm-20 mb-xs-20">
                <Col lg={6} xs={24} className="pr-lg-5">
                  <Button onClick={getTraceabilityReport} className="cmt-button primary outline" type="primary" loading={loading.traceabilityReport}>
                    Seguimiento de muestras
                  </Button>
                </Col>
              </Row>
            }
            <Row gutter={[20, 20]} align='bottom'>
              <Col lg={5} xs={24}>
                <Form.Item
                  name="dateRange"
                  label="Fecha"
                  className="mb-0"
                >
                  <RangePicker
                    locale={locale}
                    format="DD-MM-YYYY"
                    inputReadOnly
                    placeholder={['Desde', 'Hasta']}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col lg={4} xs={24}>
                <Form.Item
                  name="serviceId"
                  label={t('medicalConsultations.reportPage.serviceLabel')}
                  className="mb-0"
                >
                  <Select
                    className="cmt-select"
                    placeholder="Todos"
                    showSearch
                    showArrow={false}
                    filterOption={false}
                    allowClear
                    style={{ width: '100%', color: '#505050' }}
                    onSearch={fetchServices}
                    notFoundContent={
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={
                          <div>
                            No hay coincidencias
                          </div>
                        }
                      />
                    }
                  >
                    {
                      services?.data?.map((item, index) => {
                        return (
                          <Option
                            key={index}
                            value={`${item.id}:${item.type}`}
                            type={item.type}
                          >
                            {item.name}
                          </Option>
                        )
                      })
                    }
                  </Select>
                </Form.Item>
              </Col>
              <Col lg={10} xs={24} style={{ marginTop: 'auto' }}>
                <Form.Item
                  name="query"
                  className="mb-0 mt-lg-0 mt-md-10 mt-sm-10 mt-xs-10"
                  label="&nbsp;"
                >
                  <Input
                    className="cmt-input"
                    placeholder={t('medicalConsultations.reportPage.queryPlaceholder')}
                  />
                </Form.Item>
              </Col>
              <Col lg={5} xs={24}>
                <Spin className='cmt-spin' spinning={loading.changeStatus}>
                  {
                    (changeStatus.length > 0) &&
                    <Form.Item label='Estado de cambio' name='changeStatus' className='mb-0'>
                      <Select
                        className='cmt-select'
                        placeholder='Selecciona'
                      >
                        {
                          changeStatus.map((item) =>
                            <Option key={item.status} value={item.status}>{item.name}</Option>
                          )
                        }
                      </Select>
                    </Form.Item>
                  }
                </Spin>
              </Col>
              <Col lg={5} xs={24}>
                <Form.Item
                  name='allBranchOffices'
                  className='mb-0'
                  valuePropName='checked'
                >
                  <Checkbox className='cmt-checkbox'>
                    Mostrar todas las sucursales
                  </Checkbox>
                </Form.Item>
              </Col>
              <Col lg={5} xs={24} style={{ marginTop: 'auto' }}>
                <Row align="bottom" gutter={10}>
                  <Col>
                    <Form.Item
                      className="mb-0"
                      label="&nbsp;"
                    >
                      <Button
                        htmlType="submit"
                        className="cmt-button primary"
                        type="primary"
                      >
                        Buscar
                      </Button>
                    </Form.Item>
                  </Col>
                  <Col>
                    <Button
                      className="cmt-button primary non-outline p-0"
                      type="primary"
                      onClick={() => {
                        setFilters({});
                        form.resetFields();
                        setRefresh(Date.now());
                      }}
                    >
                      Borrar filtros
                    </Button>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Form>
        )}
      </Col>
      <Col lg={22} xs={22} className="mt-30">
        <Spin
          spinning={loading.table || loading.changesTable}
          className="cmt-spin"
        >
          {settings.report.reportsChanges[countryCode] ? (
            <>
              <Tabs className="cmt-tabs" defaultActiveKey="1">
                <TabPane tab="Informes" key="1">
                  <Col lg={24} xs={24}>
                    {
                      (reports?.data.length > 0) ?
                        <>
                          {customTableHeader()}
                          {
                            reports.data.map((item, index) => customTableBody(item, index))
                          }
                        </>
                        :
                        <Row justify="center" className="mt-50">
                          <p className="text-center">No hay informes disponibles</p>
                        </Row>
                    }
                  </Col>
                  <Col lg={24} xs={24} className="mt-20">
                    {
                      (reports?.pagination.lastPage > 0) &&
                      <Row className="pagination-wrapper mb-50">
                        <Col>
                          <Radio.Group className="cmt-pagination">
                            <Radio.Button
                              value="prev"
                              disabled={currentPage === 1}
                              onClick={loadMore}
                            >
                              Anterior
                            </Radio.Button>
                            <Radio.Button
                              value="next"
                              disabled={currentPage === reports.pagination.lastPage}
                              onClick={loadMore}
                            >
                              Siguiente
                            </Radio.Button>
                          </Radio.Group>
                        </Col>
                      </Row>
                    }
                  </Col>
                </TabPane>
                <TabPane tab="Cambios en informes" key="2">
                  <Col lg={24} xs={24}>
                    {
                      (reportChanges?.data?.length > 0) ?
                        <>
                          {reportChangesTableHeader()}
                          {
                            reportChanges.data.map((item, index) => reportChangesTableBody(item, index))
                          }
                        </>
                        :
                        <Row justify="center" className="mt-50">
                          <p className="text-center">No hay cambios en informes</p>
                        </Row>
                    }
                  </Col>
                  <Col lg={24} xs={24} className="mt-20">
                    {
                      (reportChanges?.pagination?.lastPage > 0) &&
                      <Row className="pagination-wrapper mb-50">
                        <Col>
                          <Radio.Group className="cmt-pagination">
                            <Radio.Button
                              value="prev"
                              disabled={changesCurrentPage === 1}
                              onClick={loadMoreReportChanges}
                            >
                              Anterior
                            </Radio.Button>
                            <Radio.Button
                              value="next"
                              disabled={changesCurrentPage === reportChanges?.pagination?.lastPage}
                              onClick={loadMoreReportChanges}
                            >
                              Siguiente
                            </Radio.Button>
                          </Radio.Group>
                        </Col>
                      </Row>
                    }
                  </Col>
                </TabPane>
              </Tabs>
              <FeedbackModal
                open={feedbackModal.open}
                onClose={() => {
                  setFeedbackModal(prev => ({ ...prev, open: false }));
                  fetchReportsChanges({ ...filters, page: changesCurrentPage });
                }}
                type={feedbackModal.type}
                t={t}
              />
            </>
          ) : (
            <>
              <Col lg={24} xs={24}>
                {
                  (reports?.data.length > 0) ?
                    <>
                      {customTableHeader()}
                      {
                        reports.data.map((item, index) => customTableBody(item, index))
                      }
                    </>
                    :
                    <Row justify="center" className="mt-50">
                      <p className="text-center">No hay informes disponibles</p>
                    </Row>
                }
              </Col>
              <Col lg={24} xs={24} className="mt-20">
                {
                  (reports?.pagination.lastPage > 0) &&
                  <Row className="pagination-wrapper mb-50">
                    <Col>
                      <Radio.Group className="cmt-pagination">
                        <Radio.Button
                          value="prev"
                          disabled={currentPage === 1}
                          onClick={loadMore}
                        >
                          Anterior
                        </Radio.Button>
                        <Radio.Button
                          value="next"
                          disabled={currentPage === reports.pagination.lastPage}
                          onClick={loadMore}
                        >
                          Siguiente
                        </Radio.Button>
                      </Radio.Group>
                    </Col>
                  </Row>
                }
              </Col>
            </>
          )}
        </Spin>
      </Col>
    </Row>
  );
};

export default MedicalConsultationReportPage;
